<template>
  <div class="Reports__View">
    <EntityListLayout v-if="isStoreReady" :tabs="tabs" :key="tabsKey">
      <template #title>הסבות ללקוחות</template>

      <template v-slot:counter="slotProps">
        סה"כ <b>{{ getTotalRecords(slotProps) }} </b>
      </template>
    </EntityListLayout>
  </div>
</template>

<script>
import ReportService from "@/services/ReportService";
import VehicleService from "@/services/VehicleService";
import EntityListLayout from "@/components/EntityListLayout.vue";
import TransferCompanyEntity from "@/views/TransferCompanyEntity.vue";
import ReportEntity from "@/views/ReportEntity.vue";
import TableActions from "@/services/TableActionsService";
import FieldService from "@/services/FieldService";
import FilterService from "@/services/FilterService";
import { saveAs } from "file-saver";

export default {
  name: "TransfersCompanies",
  data() {
    return {
      tabs: [],
      tabsKey: 0,
    };
  },
  components: {
    EntityListLayout,
  },
  computed: {
    isStoreReady() {
      const authReady = this.$store.state.auth?.user != null;
      const profileReady = this.$store.state.profile?.Id !== null;
      // These flags are null until profile is fetched; after load they are booleans
      const profileFlagsReady = [
        'AllowRePortImport',
        'AllowManualChangeMenu',
        'DontShowMissingContractsTab',
      ].every((k) => this.$store.state.profile[k] !== null);
      return authReady && profileReady && profileFlagsReady;
    },
    isAdmin() {
      return this.$store.state.auth?.user?.claims?.isAdmin === "True";
    },
    dateTypeOptions() {
      const base = FilterService.list().DateTypes.Options;
      if (this.isAdmin && !this.$store.state.auth.isCustomer) {
        return [
          ...base,
          { ...FilterService.DateSearchTypes.LastSendingDate },
          { ...FilterService.DateSearchTypes.LastDriverSendingDate },
        ];
      }
      return base;
    },
    tabName() {
      return this.selectedTab && this.selectedTab.title;
    },
    excelMimeType() {
      return "application/vnd.ms-excel";
    },
    profileId() {
      return +this.$store.state.auth.profileId;
    },
    allowRePortImport() {
      return this.$store.state.profile.AllowRePortImport;
    },
    dontShowMissingContractsTab() {
      return this.$store.state.profile.DontShowMissingContractsTab;
    },
    allowManualChangeMenu() {
      return this.$store.state.profile.AllowManualChangeMenu;
    },
    transfersAllowed() {
      return this.$store.state.auth?.user?.claims?.transfersAllowed === "True";
    },
    profileStartDate() {
      return this.$store.state.profile?.Settings?.StartDate;
    },
  },
  methods: {
    buildTabs() {
      const reportsFields = FieldService.list("reports");
      const reportsfilters = FilterService.list();
      const sideViewEntity = {
        component: TransferCompanyEntity,
        key: "TransferBatchItemId",
        recordUrl: "transfers",
        preview: true,
        readOnly: true,
        title: "פרטי הסבה",
      };
      const reportSideViewEntity = {
        component: ReportEntity,
        preview: true,
        readOnly: true,
        title: "פרטי דוח",
        recordUrl: "reports",
      };
      const excelExport = [
        {
          title: "יצוא טבלה",
          action: this.export,
        },
        {
          title: "יצוא מקובץ לפי עיריות",
          action: this.exportByMunic,
        },
        {
          title: "יצוא מקובץ לפי לקוח",
          action: this.exportByClient,
        },
        {
          title: "יצוא דוחות לא מנוהלים",
          action: ({ loading, query }) => {
            query.IsManaged = false;
            query.ResultType = '';
            this.exportUnmanagedReports({ loading, query });
          },
        },
      ];
      const fields = [
        { ...reportsFields.Address, checked: false },
        { ...reportsFields.Alerts, checked: false },
        { ...reportsFields.ApprovalDate, checked: false },
        { ...reportsFields.ClientComment, checked: false },
        { ...reportsFields.CreateDate, checked: false },
        { ...reportsFields.CurrentAmount, checked: true },
        { ...reportsFields.DateTaken, checked: true },
        { ...reportsFields.Driver, checked: false },
        { ...reportsFields.DriverAmount, checked: false },
        { ...reportsFields.HasScreenshot, checked: false },
        { ...reportsFields.IsPaid, checked: false },
        { ...reportsFields.IsPaidByDriver, checked: false },
        { ...reportsFields.LastPayDate, checked: true },
        { ...reportsFields.ManagerComment, checked: false },
        { ...reportsFields.MuniIdNumber, checked: false },
        { ...reportsFields.MuniReferenceNumber, checked: false },
        { ...reportsFields.Municipality, checked: true },
        { ...reportsFields.OriginalAmount, checked: true },
        { ...reportsFields.TransferredFromName, checked: true },
        { ...reportsFields.PartialPaidAmount, checked: false },
        { ...reportsFields.PaymentDate, checked: false },
        { ...reportsFields.PaymentReferenceNumber, checked: false },
        { ...reportsFields.PaymentUrl, checked: true },
        { ...reportsFields.PlateNumber, checked: true },
        { ...reportsFields.Profile, checked: true },
        { ...reportsFields.Reason, checked: false },
        { ...reportsFields.RelevantLaw, checked: false },
        { ...reportsFields.ReportCause, checked: false },
        { ...reportsFields.ReportNumber, checked: true },
        { ...reportsFields.Status, checked: true },
        { ...reportsFields.TransferCompleteDate, checked: true },
        { ...reportsFields.TransferError, checked: true },
        { ...reportsFields.TransferPdfUrl, checked: true },
        { ...reportsFields.TransferSignedDate, checked: true },
        { ...reportsFields.TransferStatus, checked: this.isAdmin, isHidden: !this.isAdmin },
        { ...reportsFields.TransferDisplayStatus, checked: true },
        { ...reportsFields.TransferredDate, checked: true },
        { ...reportsFields.LastSendingDate, checked: false, Sortable: this.isAdmin },
        { ...reportsFields.VehicleType, checked: true },
        { ...reportsFields.VerificationDate, checked: false },
        // { ...reportsFields.TransferReference, checked: true },
        { ...reportsFields.TransferReference, checked: true, Display: 'סיבת דחית הסבה' },
      ];

      this.tabs = [
        {
          isTransferContext: true,
          id: "TransfersCompanyMissingDocumentReports",
          title: "מסמכים חסרים להסבה",
          isPrimary: true,
          getDataAsync: this.getMissingDocumentReports,
          perPage: 20,
          fields,
          filters: [
            { ...reportsfilters.ReportStatus },
            { ...reportsfilters.NotReportStatus },
            { ...reportsfilters.Reason },
            { ...reportsfilters.Alerts },
            { ...reportsfilters.Profile },
            { ...reportsfilters.Driver },
            { ...reportsfilters.TransferStatusOnlyNotTransferredAndRejected },
            { ...reportsfilters.PlateNumber },
            { ...reportsfilters.OnlyOwnedVehicles },
            { ...reportsfilters.MuniIdNumber },
            { ...reportsfilters.Municipality },
            { ...reportsfilters.Other },
            { ...reportsfilters.DateTypes, Options: this.dateTypeOptions },
          ],
          sortField: "LastPayDate",
          sortOrder: "descfalse",
          checkableRows: true,
          fastSearch: true,
          sideViewEntity: reportSideViewEntity,
          excelExport,
          // tableActions: [TableActions.createTransfersCompany, TableActions.addTask],
          tableActions: (() => {
            let actions = [
              TableActions.createTransfersCompany,
              TableActions.addTask,
              TableActions.changeTransferStatusCompany,
              TableActions.markAsPaid
            ];
            if (this.isAdmin && !this.isSosnaPortal) {
              actions = actions.concat([
                {
                  ...TableActions.reportsAlertsGenerator,
                  Label: "סריקת התראות",
                },
              ]);
            }
            if (this.isAdmin || this.allowRePortImport) {
              actions = actions.concat([
                {
                  ...TableActions.updateReportsFromRePort,
                  Label: 'קליטת מסמכי הסבה ממערכת הליסינג'
                },
              ]);
            }
            if (this.allowManualChangeMenu && this.transfersAllowed) {
              actions = actions.concat([
                {
                  ...TableActions.changeManualTransferStatusCompany,
                  Label: "הוגש ידנית על ידי הלקוח",
                }
              ]);
            }
            return actions;
          })(),
        },
        // Only show Missing Contracts tab if dontShowMissingContractsTab is false
        ...(!this.dontShowMissingContractsTab ? [{
          isTransferContext: true,
          id: "TransfersCompanySignByMissingContracts",
          title: "חוזים חסרים",
          getDataAsync: this.getMissingContracts,
          perPage: 20,
          fields,
          filters: [
            { ...reportsfilters.ReportStatus },
            // { ...reportsfilters.NotReportStatus },
            { ...reportsfilters.Reason },
            { ...reportsfilters.Alerts },
            { ...reportsfilters.Profile },
            { ...reportsfilters.Driver },
            { ...reportsfilters.PlateNumber },
            { ...reportsfilters.OnlyOwnedVehicles },
            { ...reportsfilters.MuniIdNumber },
            {
              ...reportsfilters.TransferStatusWNotTransferred,
              selected: [
                0, 1, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 24,
              ],
            },
            { ...reportsfilters.Municipality },
            { ...reportsfilters.Other },
            { ...reportsfilters.DateTypes, Options: this.dateTypeOptions },
          ],
          sortField: "Id",
          sortOrder: "desc",
          checkableRows: true,
          fastSearch: true,
          excelExport,
          sideViewEntity: reportSideViewEntity,
          tableActions: (() => {
            let actions = [
              TableActions.addTask
            ];
            if (this.isAdmin) {
              actions = actions.concat([
                TableActions.changeTransferStatusCompany,
                {
                  ...TableActions.reportsAlertsGenerator,
                  Label: "סריקת התראות",
                },
              ]);
            }
            if (this.isAdmin || this.allowRePortImport) {
              actions = actions.concat([
                {
                  ...TableActions.updateReportsFromRePort,
                  Label: 'משיכת מסמכי הסבה ממערכת הליסינג'
                }
              ]);
            }
            return actions;
          })(),
        }] : []),
        {
          isTransferContext: true,
          id: "TransfersCompanySignByLawyerPending",
          title: 'ממתינים לחתימת עו"ד',
          getDataAsync: this.getTransfersViaLawyer,
          perPage: 20,
          fields: fields.filter((f) => !['TransferError', 'TransferReference', 'TransferSignedDate'].includes(f.Id)),
          filters: [
            { ...reportsfilters.NotReportStatus, selected: [0] },
            { ...reportsfilters.TransferStatus, selected: [12] },
            { ...reportsfilters.MuniIdNumber },
            { ...reportsfilters.Municipality },
            { ...reportsfilters.OnlyOwnedVehicles },
          ],
          sortField: "Id",
          sortOrder: "desc",
          checkableRows: true,
          fastSearch: true,
          sideViewEntity,
          tableActions: [TableActions.signOnReports, TableActions.addTask],
        },
        {
          id: "TransfersCompanyViaMail",
          title: "ממתינים לשליחה בדואר",
          getDataAsync: this.getTransfersViaMail,
          perPage: 20,
          fields,
          filters: [
            { ...reportsfilters.ReportStatus },
            { ...reportsfilters.NotReportStatus, selected: [0] },
            { ...reportsfilters.Reason },
            { ...reportsfilters.Alerts },
            { ...reportsfilters.Profile },
            { ...reportsfilters.Driver },
            { ...reportsfilters.TransferStatus, selected: [4, 11] },
            { ...reportsfilters.ReportNumber },
            { ...reportsfilters.MuniIdNumber },
            { ...reportsfilters.PlateNumber },
            { ...reportsfilters.OnlyOwnedVehicles },
            { ...reportsfilters.Municipality },
            { ...reportsfilters.DateTypes, Options: this.dateTypeOptions },
          ],
          sortField: "DateTaken",
          sortOrder: "desc",
          checkableRows: true,
          excelExport,
          fastSearch: true,
          sideViewEntity,
          tableActions: (() => {
            let actions = [
              TableActions.postRegisteredMail,
              TableActions.printTransfers,
              TableActions.markAsPaid,
              TableActions.addTask
            ];
            if (this.isAdmin) {
              actions = actions.concat([
                TableActions.changeTransferStatusCompany,
              ]);
            }
            return actions;
          })(),
        },
        {
          id: "TransfersCompanyHandling",
          title: "הסבות לטיפול ע”י הלקוח",
          getDataAsync: this.getReportsForHandle,
          perPage: 20,
          fields,
          filters: [
            { ...reportsfilters.ReportStatus },
            { ...reportsfilters.NotReportStatus, selected: [0] },
            { ...reportsfilters.Reason },
            { ...reportsfilters.Alerts },
            { ...reportsfilters.Profile },
            { ...reportsfilters.Driver },
            { ...reportsfilters.TransferStatus, selected: [6, 17] },
            { ...reportsfilters.ReportNumber },
            { ...reportsfilters.MuniIdNumber },
            { ...reportsfilters.PlateNumber },
            { ...reportsfilters.OnlyOwnedVehicles },
            { ...reportsfilters.Municipality },
            // If profile has StartDate, preset the DateTaken filter with it as default
            ...(this.$store.state.profile?.Settings?.StartDate
              ? [{
                  ...reportsfilters.DateTypes,
                  Options: this.dateTypeOptions,
                  selected: {
                    Value: 1, // DateTaken
                    fromDate: this.$store.state.profile.Settings.StartDate,
                  },
                }]
              : [{ ...reportsfilters.DateTypes, Options: this.dateTypeOptions }]
            ),
          ],
          sortField: "Id",
          sortOrder: "desc",
          checkableRows: true,
          fastSearch: true,
          sideViewEntity,
          excelExport,
          tableActions: (() => {
            const actions = [
              TableActions.postRegisteredMail,
              TableActions.printTransfers,
              TableActions.markAsPaid,
              TableActions.addTask,
              {
                ...TableActions.changeTransferStatusCompanyLimited3,
                Label: "שינוי סטטוס הסבת לקוח",
              },
            ];
            if (this.isAdmin && !this.isSosnaPortal) {
              actions.push(TableActions.changeReportStatus);
            }
            return actions;
          })(),
        },
      ];
      if (this.isAdmin) {
        this.tabs.push({
          id: "TransfersClarificationStatus",
          title: "הסבות לבירור סטטוס",
          getDataAsync: this.getReportsForClarification,
          perPage: 20,
          fields: fields.map((f) => (f.Id === 'LastSendingDate' ? { ...f, checked: true } : f)),
          filters: [
            { ...reportsfilters.ReportStatus },
            { ...reportsfilters.NotReportStatus, selected: [0] },
            { ...reportsfilters.Reason },
            { ...reportsfilters.Alerts },
            { ...reportsfilters.Profile },
            { ...reportsfilters.Driver },
            { ...reportsfilters.ReportNumber },
            { ...reportsfilters.MuniIdNumber },
            { ...reportsfilters.PlateNumber },
            { ...reportsfilters.OnlyOwnedVehicles },
            { ...reportsfilters.Municipality },
            {
              ...reportsfilters.TransferStatus,
              selected: [3, 4, 5, 7, 8, 15, 19, 24],
            },
            {
              ...reportsfilters.DateTypes,
              Options: [{ ...FilterService.DateSearchTypes.LastSendingDate }],
            },
          ],
          sortField: "Id",
          sortOrder: "desc",
          checkableRows: true,
          fastSearch: true,
          sideViewEntity: reportSideViewEntity,
          excelExport,
          tableActions: (() => {
            let actions = [
              TableActions.postRegisteredMail,
              TableActions.printTransfers,
              TableActions.markAsPaid,
              TableActions.changeTransferStatusCompany,
              TableActions.addTask,
            ];
            if (this.isAdmin && !this.isSosnaPortal) {
              actions = actions.concat([
                {
                  ...TableActions.reportsDetailsScanning,
                  Label: "סריקת נתוני דוחות",
                },
              ]);
            }
            return actions;
          })(),
        });
      }

      this.tabs.push(
        {
          id: "UnmanagedReports",
          title: "דוחות על רכבים לא מנוהלים",
          getDataAsync: this.getUnmanagedReports,
          perPage: 20,
          fields: [
            ...fields,
            { ...reportsFields.UnmanagedReportReason, checked: true },
          ],
          sortField: "DateTaken",
          sortOrder: "desc",
          checkableRows: true,
          sideViewEntity: reportSideViewEntity,
          excelExport: [
            {
              title: "יצוא טבלה",
              action: ({ loading, query }) => {
                query.IsManaged = false;
                query.ResultType = '';
                this.exportUnmanagedReports({ loading, query });
              },
            },
          ],
          fastSearch: true,
          fastSearchOptions: {
            propName: "ReportNumber",
            label: "חיפוש לפי מספר דוח",
          },
          tableActions: [
            TableActions.removeFromProfile,
          ],
        });

      this.setProfileIdInTab(this.profileId);
    },
    setProfileIdInTab(val) {
      if (!val) return;
      const currTab = this.tabs.find(
        (tab) => tab.id === "TransfersCompanySignByMissingContracts"
      );
      if (!currTab || !Array.isArray(currTab.filters)) return;
      const currFilter = currTab.filters.find((f) => f.Query === "ProfileIds");
      if (!currFilter) return;
      if (!Object.prototype.hasOwnProperty.call(currFilter, 'selected')) {
        this.$set(currFilter, 'selected', [val]);
      } else {
        currFilter.selected = [val];
      }
      // Don't call getDataAsync directly - EntityListLayout handles data loading
    },
    monthAdd(date, month) {
      let temp = date;
      temp = new Date(date.getFullYear(), date.getMonth(), 1);
      temp.setMonth(temp.getMonth() + (month + 1));
      temp.setDate(temp.getDate() - 1);
      if (date.getDate() < temp.getDate()) {
        temp.setDate(date.getDate());
      }
      return temp;
    },
    getTransfersViaMail({ cancelToken, setPermanentQuery, ...rest }) {
      const query = {
        IgnoreDoubleFineForTransfer: true,
        IsPaid: false,
      };
      setPermanentQuery(query);
      return ReportService.search(
        {
          ...rest,
          ...query,
        },
        cancelToken.token
      );
    },
    getAllReports({ cancelToken, ...rest }) {
      return ReportService.search(
        {
          ...rest,
        },
        cancelToken.token
      );
    },
    getMissingContracts({ cancelToken, setPermanentQuery, ...rest }) {
      const query = {
        IsPaid: false,
      };
      setPermanentQuery(query);

      return ReportService.search(
        {
          ...rest,
          ...query,
        },
        cancelToken.token
      );
    },
    getTransfersViaLawyer({ cancelToken, setPermanentQuery, ...rest }) {
      const query = {
        IsPaid: false,
      };
      setPermanentQuery(query);

      return ReportService.search(
        {
          ...rest,
          ...query,
        },
        cancelToken.token
      );
    },
    getMissingDocumentReports({ cancelToken, setPermanentQuery, ...rest }) {
      const query = {
        CanBeTransferred: true,
        IgnoreDoubleFineForTransfer: true,
        IgnoreExceptionsForTransfer: true,
        IsNotMine: true,
        IsPaid: false,
      };

      setPermanentQuery(query);

      return ReportService.search(
        {
          ...rest,
          ...query,
        },
        cancelToken.token
      );
    },
    getReportsForHandle({ cancelToken, setPermanentQuery, ...rest }) {
      const query = {
        IgnoreDoubleFineForTransfer: true,
        IsPaid: false,
      };

      setPermanentQuery(query);
      return ReportService.search(
        {
          ...rest,
          ...query,
        },
        cancelToken.token
      );
    },
    getReportsForClarification({ cancelToken, setPermanentQuery, ...rest }) {
      const query = { IsPaid: false, FilterBySendingDate: true };
      setPermanentQuery(query);
      return ReportService.search(
        {
          ...rest,
          ...query,
        },
        cancelToken.token
      );
    },

    getUnmanaged({ cancelToken }) {
      return VehicleService.unmanaged(cancelToken.token).then((r) => ({
        data: {
          TotalResults: r.data.length,
          Items: r.data,
        },
      }));
    },
    exportUnmanaged({ loading }) {
      loading(true);
      VehicleService.unmanagedXls()
        .then((r) => {
          // Check headers in multiple ways (axios normalizes to lowercase)
          const contentType = r.headers && (r.headers["content-type"] || r.headers["Content-Type"]);
          let extension = "xlsx";
          let mimetype = this.excelMimeType;
          if (contentType && contentType.toLowerCase().includes("text/csv")) {
            extension = "csv";
            mimetype = "text/csv";
          }
          this.saveFile(r.data, "יצוא רכבים לא מנוהלים ממערכת Betterway", extension, mimetype);
        })
        .catch(() => {})
        .finally(() => {
          loading(false);
        });
    },
    exportUnmanagedReports({ loading, query }) {
      loading(true);
      ReportService.exportUnmanagedReportsXsl(query)
        .then((r) => {
          // Check headers in multiple ways (axios normalizes to lowercase)
          const contentType = r.headers && (r.headers["content-type"] || r.headers["Content-Type"]);
          let extension = "xlsx";
          let mimetype = this.excelMimeType;
          if (contentType && contentType.toLowerCase().includes("text/csv")) {
            extension = "csv";
            mimetype = "text/csv";
          }
          this.saveFile(r.data, "יצוא דוחות לא מנוהלים ממערכת Betterway", extension, mimetype);
        })
        .catch((error) => {
          console.log('exportUnmanagedReports error');
          console.log(error);
        })
        .finally(() => {
          loading(false);
        });
    },
    getTotalRecords(slotProps) {
      return slotProps.tab && slotProps.tab.total && slotProps.tab.total;
    },
    saveFile(data, filename, extension = "xlsx", mimetype = null) {
      const blobType = mimetype || this.excelMimeType;
      const blob = new Blob([data], { type: blobType });
      saveAs(blob, `${filename}.${extension}`);
    },
    export({ loading, query }) {
      loading(true);
      ReportService.exportXsl(query)
        .then((r) => {
          // Check headers in multiple ways (axios normalizes to lowercase)
          const contentType = r.headers && (r.headers["content-type"] || r.headers["Content-Type"]);
          let extension = "xlsx";
          let mimetype = this.excelMimeType;
          if (contentType && contentType.toLowerCase().includes("text/csv")) {
            extension = "csv";
            mimetype = "text/csv";
          }
          this.saveFile(r.data, "יצוא דוחות הסבה ללקוח ממערכת Betterway", extension, mimetype);
        })
        .finally(() => {
          loading(false);
        });
    },
    exportByMunic({ loading, query }) {
      loading(true);
      ReportService.exportXsl(query, "Municipalities")
        .then((r) => {
          // Check headers in multiple ways (axios normalizes to lowercase)
          const contentType = r.headers && (r.headers["content-type"] || r.headers["Content-Type"]);
          let extension = "xlsx";
          let mimetype = this.excelMimeType;
          if (contentType && contentType.toLowerCase().includes("text/csv")) {
            extension = "csv";
            mimetype = "text/csv";
          }
          this.saveFile(
            r.data,
            "יצוא דוחות הסבה ללקוח ממערכת Betterway מקובץ לפי עיריות",
            extension,
            mimetype
          );
        })
        .catch(() => { })
        .finally(() => {
          loading(false);
        });
    },
    exportByClient({ loading, query }) {
      loading(true);
      ReportService.exportXsl(query, "Profiles")
        .then((r) => {
          // Check headers in multiple ways (axios normalizes to lowercase)
          const contentType = r.headers && (r.headers["content-type"] || r.headers["Content-Type"]);
          let extension = "xlsx";
          let mimetype = this.excelMimeType;
          if (contentType && contentType.toLowerCase().includes("text/csv")) {
            extension = "csv";
            mimetype = "text/csv";
          }
          this.saveFile(
            r.data,
            "יצוא דוחות הסבה ללקוח ממערכת Betterway מקובץ לפי לקוחות",
            extension,
            mimetype
          );
        })
        .catch(() => { })
        .finally(() => {
          loading(false);
        });
    },
    async getUnmanagedReports({ cancelToken, ...rest }) {
      const response = await ReportService.getUnmanagedReports(rest, cancelToken.token);
      // Check if response.data is array or has Items property
      const items = Array.isArray(response.data) ? response.data : response.data.Items || [];
      // Use TotalResults if > 0, otherwise use items.length (for current page count)
      const total = (response.data.TotalResults && response.data.TotalResults > 0)
        ? response.data.TotalResults
        : items.length;
      return {
        data: {
          TotalResults: total,
          Items: items,
        },
      };
    },
  },
  watch: {
    profileId(newValue) {
      if (!this.tabs.length) return;
      this.setProfileIdInTab(newValue);
    },
    isStoreReady: {
      immediate: true,
      handler(ready) {
        if (ready && !this.tabs.length) this.buildTabs();
      }
    },
    profileStartDate(newValue, oldValue) {
      // Only rebuild if StartDate was loaded after tabs were already built
      if (newValue && !oldValue && this.tabs.length) {
        console.log('StartDate loaded after tabs built, rebuilding...', newValue);
        // Clear tabs and rebuild, increment key to force complete re-mount
        this.tabs = [];
        this.$nextTick(() => {
          this.buildTabs();
          this.tabsKey += 1; // Force EntityListLayout to re-mount and re-select tab from URL
        });
      }
    },
  },
};
</script>
